/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import * as dcmjs from 'dcmjs';
import { mat3, mat4, ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import { formatDate } from '~/utils/date';
import { DcmJsDatasetWrapper } from '../dicom/dcmJsWrapper';
import { DicomBase } from '../dicom/dicomBase';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import {
    DicomDvh,
    DicomDose,
    strToDoseSummationType,
    strToDoseType,
    strToDoseUnit,
    strToDvhType,
    strToVolumeUnits,
} from '../dicom/dicomDose';
import { DicomPatient } from '../dicom/dicomPatient';
import { DicomPlan } from '../dicom/dicomPlan';
import { DicomRegistration } from '../dicom/dicomRegistration';
import { DicomRigidRegistration } from '../dicom/dicomRigidRegistration';
import {
    DicomContour,
    DicomStructureSet,
    RoiGenerationAlgorithmType,
} from '../dicom/dicomstructureset';
import { DicomRoi } from '../dicom/dicomRoi';
import { DicomImageSlice } from '../dicom/dicomImageSlice';
import { Modality, StorageSOPClassUID, strToModality } from '../dicom/modality';
import { PatientDataSet } from '../dicom/patientdataset';
import { DicomSeries, Series } from '../dicom/series';
import {
    getDICOMDate,
    getDICOMTime,
    mat4ToRowMajorArray,
    roundToDecimals,
} from '../dicom/utils';
import { DoseMatrix } from '../patient/doseMatrix';
import {
    Image3D,
    SliceValidationStatus,
    validateSlicePositions,
} from '../patient/image3d';
import { completeHydration } from '../patient/hydration';
import { Patient } from '../patient/patient';
import { Plan } from '../patient/plan';
import { RigidRegistration } from '../patient/rigidRegistration';
import { StructureSet } from '../patient/structureset';
import { PatientRepository } from '../repository/patientRepository';
import { HeaderHelper } from './headerHelper';
import { generateUID, isUidLike } from '~/utils/dicomUid';
import AppConfig from '~/utils/appconfig';
import { WindowLevel } from '../patient/windowLevel';
import { hasAsciiCharactersOnly } from '~/utils/validation';
import {
    generateRandomDicomColor,
    generateRandomRgbColor,
} from '~/utils/color';
import { localization } from '~/i18n';
import {
    isDicomPlan,
    isDicomRigidRegistration,
    isDicomStructureSet,
    isDose,
} from '../dicom/dicomTypeGuards';
import { Image3DOptions } from '../patient/sliceInterpolation';
import { domainTrace } from '../diagnostics/domainTrace';

/** Class for processing DICOM files */

/**
 * Information about an image that required slice interpolation
 */
export type InterpolatedImageInfo = {
    /** Series instance UID */
    seriesUid: string;
    /** Series description */
    seriesDescription: string;
    /** Number of slices that were interpolated */
    interpolatedSliceCount: number;
};

export type SkippedSeriesInfo = {
    /** Series instance UID */
    seriesUid: string;
    /** Series description (human-readable) */
    seriesDescription: string;
    /** Short, user-facing reason the series was skipped */
    reason: string;
};

// encapsulation of imported objects
export type ImportResult = {
    patients: PatientDataSet[];
    images: Image3D[];
    doses: DoseMatrix[];
    structureSets: StructureSet[];
    registrations: RigidRegistration[];
    /** Images that had missing slices filled via interpolation */
    interpolatedImages: InterpolatedImageInfo[];
    /** Image series that could not be reconstructed into 3D volumes */
    skippedSeries: SkippedSeriesInfo[];
};

/**
 * Union type representing all supported DICOM object types that can be processed by the DICOM processor.
 */
type ProcessibleDicomObject =
    | DicomImageSlice
    | DoseMatrix
    | StructureSet
    | RigidRegistration
    | Plan;

/**
 * Enum representing the Transfer Syntax Unique Identifiers (UIDs) used in DICOM.
 *
 * See: https://www.dicomlibrary.com/dicom/transfer-syntax/
 */
export enum TransferSyntaxUID {
    ImplicitVRLittleEndian = '1.2.840.10008.1.2',
    ExplicitVRLittleEndian = '1.2.840.10008.1.2.1',
}

export class DicomProcessor {
    /**
     * A set that tracks the SOP Instance UIDs of DICOM images that have already been processed.
     * This prevents duplicate processing of the same DICOM instance.
     */
    private processedSopUids: Set<string> = new Set();

    public static SupportedImagePixelTransferSyntaxes = [
        TransferSyntaxUID.ImplicitVRLittleEndian,
        TransferSyntaxUID.ExplicitVRLittleEndian,
    ];

    public processDicomData(arrayBuffer: ArrayBuffer): DicomBase | null {
        domainTrace.dicom('processDicomData', {
            bytes: arrayBuffer.byteLength,
        });
        const dataset = DicomProcessor.readDicomDataset(arrayBuffer);

        switch (dataset.Modality) {
            case 'CT':
            case 'MR':
            case 'PT':
                return this.dicomToImageSlice(dataset);
            case 'RTSTRUCT':
                return this.dicomToStructureSet(dataset);
            case 'RTDOSE':
                return this.dicomToDose(dataset);
            case 'REG':
                return this.dicomToRegistration(dataset);
            case 'RTPLAN':
                return this.dicomToPlan(dataset);
            default:
                console.warn(
                    `Skipping DICOM file with unsupported` +
                        ` or missing modality:` +
                        ` ${dataset.Modality ?? 'undefined'}`
                );
                break;
        }

        return null;
    }

    public dicomToImageSlice(dataset: any): DicomImageSlice | null {
        const imageDataset = this.readImageDataset(dataset);
        if (imageDataset != null) {
            return this.storeImageSlice(imageDataset);
        }
        return null;
    }

    /** Generates a new DICOM UID. */
    public static generateUid(): string {
        return dcmjs.data.DicomMetaDictionary.uid();
    }

    /**
     * Parses ROIs from a DcmJsDatasetWrapper by delegating to the existing
     * parseRois function. Used during structure set duplication where we have
     * a cloned dataset and need to reconstitute the ROI map.
     *
     * @param wrapper - A DcmJsDatasetWrapper containing RTSTRUCT data
     * @returns Map of ROI number → DicomRoi
     */
    public static parseRoisFromDataset(
        wrapper: DcmJsDatasetWrapper
    ): Map<number, DicomRoi> {
        const referencedSopUids: string[] = [];
        return parseRois(wrapper, referencedSopUids);
    }

    /**
     * Sets the SOP Instance UID (0008,0018).
     * Also updates MediaStorageSOPInstanceUID in the file meta header
     * to keep them consistent per DICOM PS3.10.
     */
    public static setSopInstanceUid(
        wrapper: DcmJsDatasetWrapper,
        uid: string
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (wrapper.dataset as any).SOPInstanceUID = uid;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const meta = (wrapper.dataset as any)._meta;
        if (meta?.MediaStorageSOPInstanceUID?.Value) {
            meta.MediaStorageSOPInstanceUID.Value = [uid];
        }
    }

    /**
     * Sets the Series Instance UID (0020,000E).
     */
    public static setSeriesInstanceUid(
        wrapper: DcmJsDatasetWrapper,
        uid: string
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (wrapper.dataset as any).SeriesInstanceUID = uid;
    }

    /**
     * Sets the Frame of Reference UID (0020,0052) at the top level
     * and within the Referenced Frame of Reference Sequence (3006,0010).
     * Both must stay in sync for a valid RTSTRUCT.
     */
    public static setFrameOfReferenceUid(
        wrapper: DcmJsDatasetWrapper,
        uid: string
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (wrapper.dataset as any).FrameOfReferenceUID = uid;

        const rforItem =
            DicomProcessor.getReferencedFrameOfReferenceItem(wrapper);
        if (rforItem) {
            rforItem.FrameOfReferenceUID = uid;
        }
    }

    /**
     * Sets the Series Instance UID within the RT Referenced Series Sequence.
     *
     * DICOM path:
     *   Referenced Frame of Reference Sequence (3006,0010)
     *     └─ RT Referenced Study Sequence (3006,0012)
     *         └─ RT Referenced Series Sequence (3006,0014)
     *             └─ Series Instance UID (0020,000E)
     */
    public static setReferencedSeriesUid(
        wrapper: DcmJsDatasetWrapper,
        uid: string
    ): void {
        const rtRefSeries = DicomProcessor.getRTReferencedSeriesItem(wrapper);
        if (rtRefSeries) {
            rtRefSeries.SeriesInstanceUID = uid;
        }
    }

    /**
     * Sets the approval status, approver name, and approval date/time in the dataset.
     * @param wrapper The dataset wrapper to update
     * @param status The approval status to set.
     * @param user The name of the approver.
     */
    public static setApprovalStatus(
        wrapper: DcmJsDatasetWrapper,
        status: DICOMApprovalStatus,
        user?: string
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (wrapper.dataset as any).ApprovalStatus = status;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (wrapper.dataset as any).ApprovalDateTime =
            getDICOMDate() + getDICOMTime();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (wrapper.dataset as any).ApproverName = user ?? 'unknown';
    }

    /**
     * Replaces the Contour Image Sequence within the RT Referenced Series
     * Sequence to reference the target image's slices.
     *
     * This is the dataset-level reference list telling DICOM viewers which
     * image slices the structure set is associated with. Per-contour
     * references in the ROI Contour Sequence are handled separately.
     *
     * @param wrapper The dataset wrapper to update
     * @param seriesUid - Series Instance UID of the target image
     * @param sopClassUid - SOP Class UID of the target image slices
     * @param sopInstanceUids - SOP Instance UIDs of all slices in the target image
     */
    public static updateReferencedImageUids(
        wrapper: DcmJsDatasetWrapper,
        seriesUid: string,
        sopClassUid: string,
        sopInstanceUids: string[]
    ): void {
        const rtRefSeries = DicomProcessor.getRTReferencedSeriesItem(wrapper);
        if (!rtRefSeries) {
            console.warn(
                'Cannot update referenced image UIDs: ' +
                    'RT Referenced Series Sequence not found in dataset.'
            );
            return;
        }

        rtRefSeries.SeriesInstanceUID = seriesUid;
        rtRefSeries.ContourImageSequence = sopInstanceUids.map((uid) => ({
            ReferencedSOPClassUID: sopClassUid,
            ReferencedSOPInstanceUID: uid,
        }));
    }

    /**
     * Gets the first item of the Referenced Frame of Reference Sequence.
     *
     * dcmjs naturalizes single-item sequences as plain objects and
     * multi-item sequences as arrays. This helper handles both forms.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static getReferencedFrameOfReferenceItem(
        wrapper: DcmJsDatasetWrapper
    ): any | undefined {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const seq = (wrapper.dataset as any).ReferencedFrameOfReferenceSequence;
        if (!seq) {
            return undefined;
        }
        return Array.isArray(seq) ? seq[0] : seq;
    }

    /**
     * Navigates to the RT Referenced Series Sequence item inside the
     * Referenced Frame of Reference Sequence.
     *
     * Full DICOM path:
     *   ReferencedFrameOfReferenceSequence
     *     └─ RTReferencedStudySequence
     *         └─ RTReferencedSeriesSequence
     *
     * Handles both array and non-array dcmjs naturalizations at each level.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static getRTReferencedSeriesItem(
        wrapper: DcmJsDatasetWrapper
    ): any | undefined {
        const rforItem =
            DicomProcessor.getReferencedFrameOfReferenceItem(wrapper);
        if (!rforItem) {
            return undefined;
        }

        const rtStudy = rforItem.RTReferencedStudySequence;
        if (!rtStudy) {
            return undefined;
        }
        const rtStudyItem = Array.isArray(rtStudy) ? rtStudy[0] : rtStudy;

        const rtSeries = rtStudyItem?.RTReferencedSeriesSequence;
        if (!rtSeries) {
            return undefined;
        }
        return Array.isArray(rtSeries) ? rtSeries[0] : rtSeries;
    }

    private markAsProcessed(sopInstanceUid: string): boolean {
        if (this.processedSopUids.has(sopInstanceUid)) {
            return false; // Already processed
        }
        this.processedSopUids.add(sopInstanceUid);
        return true; // Marked as processed
    }

    private hasBeenProcessed(sopInstanceUid: string): boolean {
        return this.processedSopUids.has(sopInstanceUid);
    }

    /** Adds a new 3D image from given series to the patient if it does not already exist.
     * Note: this method is not suitable for progressive addition of slices to existing images.
     *
     * @param series The series to create an image from
     * @param patient The patient to add the image to
     * @param newImages Array to collect newly created images
     * @param interpolatedImages Array to collect info about images that required interpolation
     * @param skippedSeries Array to collect info about series that could not be reconstructed
     */
    private addNewImage3D(
        series: Series,
        patient: Patient,
        newImages: Image3D[],
        interpolatedImages: InterpolatedImageInfo[],
        skippedSeries: SkippedSeriesInfo[]
    ) {
        if (!series.is3DImage) {
            throw new Error(
                `Expected series ${series.seriesInstanceUID} to be a 3D image series, got ${series.seriesModule.modality} instead.`
            );
        }

        const existingImage = patient.getImage3DWithDicomUid(
            series.seriesInstanceUID
        );

        if (!existingImage) {
            const slices = series.seriesObjects as DicomImageSlice[];
            const slicesHaveNotBeenProcessedAlready = slices.every(
                (slice) => !this.hasBeenProcessed(slice.sopInstanceUid)
            );

            // stop here if we have some slices we've already processed
            if (!slicesHaveNotBeenProcessedAlready) {
                console.warn(
                    `At least some slices from series ${series.seriesInstanceUID} have already been processed -- skipping creation of new 3D image.`
                );
                return;
            }

            // Validate slice positions before attempting to create the image
            const validation = validateSlicePositions(series);
            let image3dOptions: Image3DOptions | undefined;

            if (validation.status === SliceValidationStatus.MissingSlices) {
                image3dOptions = {
                    interpolateMissingSlices: true,
                    maxInterpolatedSlices: Image3D.MaxAutoInterpolatedSlices,
                };
            }

            try {
                const image = Image3D.fromSeries(series, image3dOptions);
                if (image) {
                    patient.addImage(image);
                    newImages.push(image);

                    // Track if interpolation was used
                    if (image.hasInterpolatedSlices) {
                        interpolatedImages.push({
                            seriesUid: series.seriesInstanceUID,
                            seriesDescription:
                                series.seriesModule.seriesDescription || '',
                            interpolatedSliceCount:
                                image.interpolatedSliceCount,
                        });
                    }
                } else {
                    console.warn(
                        `[addNewImage3D] Image3D.fromSeries returned null for series ${series.seriesInstanceUID}`
                    );
                }
            } catch (error) {
                // Log the full technical details for debugging
                console.warn(
                    `Image3D construction failed for series ` +
                        `${series.seriesInstanceUID}: ${error instanceof Error ? error.message : error}`
                );

                // Build a short, user-facing reason
                const description = series.seriesModule.seriesDescription || '';
                let reason: string;
                if (
                    validation.status === SliceValidationStatus.ResamplingNeeded
                ) {
                    reason =
                        'Non-uniform slice spacing — use Reformat to correct';
                } else if (
                    validation.status === SliceValidationStatus.MissingSlices
                ) {
                    reason = `Too many missing slices (${validation.missingSliceCount}) — use Reformat to correct`;
                } else {
                    reason = 'Could not reconstruct 3D volume from slices';
                }

                skippedSeries.push({
                    seriesUid: series.seriesInstanceUID,
                    seriesDescription: description,
                    reason,
                });
            }

            // mark all slices as processed (even if the image creation above failed)
            slices.forEach((slice) =>
                this.markAsProcessed(slice.sopInstanceUid)
            );
        }
    }

    /**
     * Adds new DICOM objects to a patient/series.
     * The type of newDicomObjects must match the contained seriesObjects
     * (e.g. StructureSet[] and DicomStructureSet).
     */
    private addNewDicomObject(
        series: Series,
        patient: Patient,
        newDicomObjects?: ProcessibleDicomObject[]
    ): void {
        for (const seriesObject of series.seriesObjects) {
            if (!this.hasBeenProcessed(seriesObject.sopInstanceUid)) {
                const existingItem = patient.getSeriesItemWithSopInstanceUid(
                    series,
                    seriesObject.sopInstanceUid
                );

                if (!existingItem) {
                    // Dose
                    if (
                        series.isDoseSeries &&
                        isDose(seriesObject) &&
                        isDoseMatrixOrEmptyArray(newDicomObjects)
                    ) {
                        this.addNewDose(seriesObject, patient, newDicomObjects);
                    }
                    // StructureSet
                    else if (
                        series.isStructuresetSeries &&
                        isDicomStructureSet(seriesObject) &&
                        isStructureSetOrEmptyArray(newDicomObjects)
                    ) {
                        this.addNewStructureSet(
                            seriesObject,
                            patient,
                            newDicomObjects as StructureSet[]
                        );
                    }
                    // Plan
                    else if (series.isPlanSeries && isDicomPlan(seriesObject)) {
                        this.addNewPlan(seriesObject, patient);
                    }
                    // Rigid Registration
                    else if (
                        series.isRegistrationSeries &&
                        isDicomRigidRegistration(seriesObject) &&
                        isRigidRegistrationOrEmptyArray(newDicomObjects)
                    ) {
                        this.addNewRigidRegistration(
                            seriesObject,
                            patient,
                            newDicomObjects as RigidRegistration[]
                        );
                    }
                    // Unsupported types
                    else {
                        throw new Error(
                            `Type mismatch or unsupported DICOM object in addNewDicomObject for series ${series.seriesInstanceUID} with modality ${series.seriesModule.modality}. Unable to process object with SOP Instance UID ${seriesObject.sopInstanceUid}`
                        );
                    }

                    this.markAsProcessed(seriesObject.sopInstanceUid);
                } else {
                    // The object is already in the patient aggregate but was
                    // never recorded as processed by *this* processor. This
                    // happens for structure sets authored in-session
                    // (StructureSetService.createEmpty adds to the aggregate
                    // without going through ingest), and for any re-import via
                    // a fresh processor. Re-importing the same SOP Instance UID
                    // is idempotent (same object identity), so mark it
                    // processed and skip — throwing here would brick every
                    // subsequent import for the rest of the session, because
                    // the throw precedes markAsProcessed above.
                    this.markAsProcessed(seriesObject.sopInstanceUid);
                }
            }
        }
    }

    private addNewDose(
        dicomDose: DicomDose,
        patient: Patient,
        newDoses: DoseMatrix[]
    ) {
        const doseMatrix = DoseMatrix.fromDose(dicomDose);
        patient.addDose(doseMatrix);
        newDoses.push(doseMatrix);
    }

    private addNewStructureSet(
        dicomStructureSet: DicomStructureSet,
        patient: Patient,
        newStructureSets: StructureSet[]
    ) {
        const structureSet = new StructureSet(
            dicomStructureSet,
            dicomStructureSet.frameOfReferenceUid,
            null
        );

        // Sync all ROI approval statuses to match structure set approval status
        // Since ROIs don't store approval status in DICOM, they inherit from structure set
        const structureSetApprovalStatus = structureSet.approvalStatus;
        structureSet.roiArray.forEach((roi) => {
            roi.approvalStatus = structureSetApprovalStatus;
        });

        patient.addStructureSet(structureSet);
        newStructureSets.push(structureSet);
    }

    private addNewPlan(dicomPlan: DicomPlan, patient: Patient) {
        patient.addPlan(
            new Plan(
                dicomPlan,
                dicomPlan.sopInstanceUid,
                dicomPlan.rtPlanLabel,
                dicomPlan.referencedRtStructureSetUid
            )
        );
    }

    private addNewRigidRegistration(
        dicomRigidRegistration: DicomRigidRegistration,
        patient: Patient,
        newRigidRegistrations: RigidRegistration[]
    ) {
        const rigidRegistration = new RigidRegistration(
            dicomRigidRegistration.sourceFrameOfReferenceUid,
            dicomRigidRegistration.targetFrameOfReferenceUid,
            dicomRigidRegistration.transformation,
            dicomRigidRegistration
        );

        patient.addRegistration(rigidRegistration);
        newRigidRegistrations.push(rigidRegistration);
    }

    public toRepository(
        patientRepository: PatientRepository,
        options?: { allowMultiplePatients?: boolean }
    ): ImportResult {
        if (!options?.allowMultiplePatients) {
            const incomingIds = this.getPatientIds();
            const loadedPatients = patientRepository.getAll();

            if (incomingIds.length > 1) {
                throw new Error(
                    'Cannot commit multiple patients to the' +
                        ' repository. Patient' +
                        ` IDs: ${incomingIds.join(', ')}`
                );
            }

            if (loadedPatients.length > 0 && incomingIds.length > 0) {
                const loadedIds = new Set(
                    loadedPatients.map((p) => p.dataSet.PatientID)
                );
                const conflicting = incomingIds.filter(
                    (id) => !loadedIds.has(id)
                );
                if (conflicting.length > 0) {
                    throw new Error(
                        'Cannot commit data for a different' +
                            ' patient when another patient is' +
                            ' already loaded.' +
                            ` Loaded: ${Array.from(loadedIds).join(', ')},` +
                            ` incoming: ${conflicting.join(', ')}`
                    );
                }
            }
        }

        const newPatients = new Array<PatientDataSet>();
        const newImages = new Array<Image3D>();
        const newDoses = new Array<DoseMatrix>();
        const newStructureSets = new Array<StructureSet>();
        const newRegistrations = new Array<RigidRegistration>();
        const interpolatedImages = new Array<InterpolatedImageInfo>();
        const skippedSeries = new Array<SkippedSeriesInfo>();

        for (const patient of this.patients) {
            let existingPatient = patientRepository.getByPatientId(
                patient.PatientID
            );
            if (!existingPatient) {
                existingPatient = new Patient(patient);
                patientRepository.add(existingPatient, options);
                newPatients.push(patient);
            }

            const imagesCountBefore = newImages.length;
            const dosesCountBefore = newDoses.length;
            const structureSetsCountBefore = newStructureSets.length;
            const registrationsCountBefore = newRegistrations.length;

            for (const series of patient.series) {
                if (series.is3DImage) {
                    this.addNewImage3D(
                        series,
                        existingPatient,
                        newImages,
                        interpolatedImages,
                        skippedSeries
                    );
                } else if (series.isDoseSeries) {
                    this.addNewDicomObject(series, existingPatient, newDoses);
                } else if (series.isStructuresetSeries) {
                    this.addNewDicomObject(
                        series,
                        existingPatient,
                        newStructureSets
                    );
                } else if (series.isPlanSeries) {
                    this.addNewDicomObject(series, existingPatient);
                } else if (series.isRegistrationSeries) {
                    this.addNewDicomObject(
                        series,
                        existingPatient,
                        newRegistrations
                    );
                }
            }

            domainTrace.dicom('toRepository', {
                patientId: existingPatient.id,
                structureSets:
                    newStructureSets.length - structureSetsCountBefore,
                images: newImages.length - imagesCountBefore,
                doses: newDoses.length - dosesCountBefore,
                registrations:
                    newRegistrations.length - registrationsCountBefore,
            });

            completeHydration(existingPatient);
            this.connectObjects(existingPatient);
        }
        return {
            patients: newPatients,
            images: newImages,
            doses: newDoses,
            structureSets: newStructureSets,
            registrations: newRegistrations,
            interpolatedImages,
            skippedSeries,
        };
    }

    private connectObjects(patient: Patient) {
        for (const ss of patient.structureSets) {
            if (!ss.image) {
                findImage(ss);
            }
        }

        function findImage(ss: StructureSet) {
            for (const image of patient.images) {
                // Connect by structure set Referenced Frame of Reference Sequence, RT Referenced Series attribute
                if (
                    image.frameOfReferenceUid === ss.frameOfReferenceUid &&
                    image.seriesUid === ss.dicomStructureSet.referencedSeriesUid
                ) {
                    ss.image = image;
                    return;
                }
                // Connect by Referenced SOP Instance UID in Contour Image Sequence
                for (const refSliceUid of ss.dicomStructureSet
                    .referencedSopUids) {
                    if (image.hasSlice(refSliceUid)) {
                        ss.image = image;
                        return;
                    }
                }
            }
        }

        for (const dose of patient.doses) {
            if (!dose.plan) {
                for (const plan of patient.plans) {
                    if (dose.referencesPlanUid(plan.sopInstanceUid)) {
                        dose.plan = plan;
                        plan.doseMatrix = dose;
                        break;
                    }
                }
                if (!dose.plan && dose.dicomDose?.referencedRtStructureSetUid) {
                    for (const ss of patient.structureSets) {
                        if (
                            dose.dicomDose?.referencedRtStructureSetUid ===
                            ss.dicomStructureSet.sopInstanceUid
                        ) {
                            dose.structureSet = ss;
                        }
                    }
                }
            }
        }

        for (const plan of patient.plans) {
            if (!plan.structureSet) {
                for (const ss of patient.structureSets) {
                    if (
                        plan.referencedStructureSetUid ===
                        ss.dicomStructureSet.sopInstanceUid
                    ) {
                        plan.structureSet = ss;
                    }
                }
            }
        }
    }

    private readImageDataset(imageDataset: any): any {
        if (
            !imageDataset.PixelSpacing ||
            !imageDataset.ImagePositionPatient ||
            !imageDataset.ImageOrientationPatient
        ) {
            throw new Error(
                'Missing required fields in DICOM dataset',
                imageDataset
            );
        }
        if (
            !DicomProcessor.SupportedImagePixelTransferSyntaxes.some(
                (x) => x === imageDataset._meta.TransferSyntaxUID.Value[0]
            )
        ) {
            throw new Error(
                `Unsupported transfer syntax '${imageDataset._meta.TransferSyntaxUID.Value[0]}'. Compressed image data is not supported.`,
                imageDataset
            );
        }
        return imageDataset;
    }

    public static readDicomDataset(arrayBuffer: ArrayBuffer): any {
        const dicomData = dcmjs.data.DicomMessage.readFile(
            HeaderHelper.ensureValidDicom(arrayBuffer)
        );
        const dicomDataset = dcmjs.data.DicomMetaDictionary.naturalizeDataset(
            dicomData.dict
        );
        dicomDataset._meta = dcmjs.data.DicomMetaDictionary.namifyDataset(
            dicomData.meta
        );
        return dicomDataset;
    }

    public static cloneDicomDataset(dicomBase: DicomBase): any {
        // Shallow-clone to prevent datasetToDict() from mutating
        // the source dataset's _meta in-place
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const clone = { ...(dicomBase.datasetWrapper.dataset as any) };
        const datasetBuf = dcmjs.data.datasetToDict(clone).write();
        const datasetClone = DicomProcessor.readDicomDataset(datasetBuf);
        return datasetClone;
    }

    public dicomToStructureSet(dicomDataset: any): DicomStructureSet | null {
        if (dicomDataset != null) {
            return this.storeStructureSet(dicomDataset);
        }
        return null;
    }

    public dicomToDose(dataset: any): DicomDose | null {
        if (
            !dataset.PixelSpacing ||
            !dataset.ImagePositionPatient ||
            !dataset.ImageOrientationPatient ||
            !dataset.DoseType ||
            !dataset.DoseUnits
        ) {
            return null; // This dicom file is not a dose
        }
        return this.storeDose(dataset);
    }

    public dicomToRegistration(dataset: any): DicomRegistration | null {
        return dataset !== null ? this.storeRegistration(dataset) : null;
    }

    public dicomToPlan(dataset: any): DicomPlan | null {
        return dataset !== null ? this.storePlan(dataset) : null;
    }

    /**
     * Creates an empty default DICOM RT Structure Set for a given patient and 3D image.
     *
     * This method generates a new RT Structure Set dataset based on the provided
     * patient and image data. It ensures that the dataset adheres to the DICOM
     * standard and includes all necessary metadata for the RT Structure Set.
     *
     * @param patient - The patient for whom the RT Structure Set is being created.
     * @param image - The 3D image used as the basis for the RT Structure Set.
     */
    public static createEmptyDefaultDicomStructureSet(
        patient: Patient,
        image: Image3D
    ): DicomStructureSet {
        const { sopClassUid, frameOfReferenceUid } = image;

        const date = getDICOMDate();
        const time = getDICOMTime();

        // try to determine the referenced SOP Class UID from the image modality if not provided
        const referencedSopClassUid = sopClassUid
            ? sopClassUid
            : image.seriesMetadata.modality === Modality.CT
              ? StorageSOPClassUID.CTImageStorage
              : image.seriesMetadata.modality === Modality.MR
                ? StorageSOPClassUID.MRImageStorage
                : image.seriesMetadata.modality === Modality.PT
                  ? StorageSOPClassUID.PETImageStorage
                  : undefined;

        if (!referencedSopClassUid) {
            throw new Error(
                `Could not determine referenced SOP Class UID for image series ${image.seriesUid}` +
                    ` -- modality ${image.seriesMetadata.modality} not supported for RT Structure Set creation.`
            );
        }

        // check if we can reuse an existing series UID if
        // 1. we already have a series containing RTSTRUCTs for the same frame of reference,
        // 2. the series contains any RTSTRUCTs created by MVision
        const firstMVisionStructureSetFromSameFOR = patient.structureSets.find(
            (ss) =>
                ss.frameOfReferenceUid === frameOfReferenceUid &&
                ss.manufacturer.toLowerCase().includes('mvision')
        );

        const rtSopUid = generateUID();
        const rtSeriesUid = firstMVisionStructureSetFromSameFOR
            ? firstMVisionStructureSetFromSameFOR.seriesInstanceUid
            : generateUID();
        const referencedSeriesUid = image.seriesUid;
        const referencedSopUids = image.imageSlices.map(
            (slice) => slice.sopInstanceUid
        );

        const imageSeriesDescription =
            image.seriesMetadata.seriesDescription.trim();
        const label =
            imageSeriesDescription &&
            hasAsciiCharactersOnly(imageSeriesDescription)
                ? imageSeriesDescription
                : AppConfig.defaultNewStructureSetLabel;
        const structureSets = patient.structureSets?.filter(
            (ss) => ss.image?.seriesUid === image.seriesUid
        );

        const structureSetLabel = generateUniqueStructureSetLabel(
            structureSets,
            label
        );

        const structureSetSpecificFields: unknown = {
            StructureSetLabel: structureSetLabel,
            StructureSetDate: date,
            StructureSetTime: time,

            ReferencedFrameOfReferenceSequence: [
                {
                    FrameOfReferenceUID: frameOfReferenceUid,
                    RTReferencedStudySequence: {
                        ReferencedSOPClassUID:
                            StorageSOPClassUID.RTStructureSetStorage,
                        ReferencedSOPInstanceUID: image.studyUid,
                        RTReferencedSeriesSequence: {
                            SeriesInstanceUID: referencedSeriesUid,
                            ContourImageSequence: referencedSopUids.map(
                                (referencedSopUid) => ({
                                    ReferencedSOPClassUID:
                                        referencedSopClassUid,
                                    ReferencedSOPInstanceUID: referencedSopUid,
                                })
                            ),
                        },
                    },
                },
            ],
            RTROIObservationsSequence: [], // ObservationNumber, ReferencedROINumber, ROIObservationLabel, RTROIInterpretedType, ROIInterpreter
            StructureSetROISequence: [], // #ROINumber, ROIName, ROIGenerationAlgorithm, ReferencedFrameOfReferenceUID
            ROIContourSequence: [], // ReferencedROINumber, ROIDisplayColor, ContourSequence []
        };

        // create dataset
        const dataset = DicomProcessor.createDcmjsDataset(
            patient,
            image,
            Modality.RTSTRUCT,
            StorageSOPClassUID.RTStructureSetStorage,
            rtSopUid,
            rtSeriesUid,
            structureSetSpecificFields
        );

        const dicomStructureSet = new DicomStructureSet(
            dataset,
            rtSopUid,
            frameOfReferenceUid,
            referencedSeriesUid,
            undefined,
            referencedSopUids,
            referencedSopClassUid,
            rtSeriesUid
        );

        return dicomStructureSet;
    }

    /**
     * Creates a new empty DICOM Spatial Registration object
     * with an identity transformation matrix.
     *
     * @param patient - The patient for whom the registration is being created.
     * @param primaryImage - The primary (fixed/reference) image.
     * @param secondaryImage - The secondary (moving) image.
     * @param contentLabel - Label for the registration.
     * @param transformation - The 4x4 transformation matrix (defaults to identity).
     * @param contentCreatorName - Name of the user creating this registration.
     * @returns A DicomRigidRegistration with a properly initialized DICOM dataset.
     */
    public static createEmptyDicomRegistration(
        patient: Patient,
        primaryImage: Image3D,
        secondaryImage: Image3D,
        contentLabel: string,
        transformation: mat4 = mat4.create(),
        contentCreatorName: string = ''
    ): DicomRigidRegistration {
        const sopInstanceUid = generateUID();
        const seriesInstanceUid = generateUID();

        const buildReferencedSeriesItem = (image: Image3D) => ({
            SeriesInstanceUID: image.seriesUid,
            ReferencedInstanceSequence: image.imageSlices.map((slice) => ({
                ReferencedSOPClassUID: image.sopClassUid,
                ReferencedSOPInstanceUID: slice.sopInstanceUid,
            })),
        });

        const registrationSpecificFields = {
            ContentLabel: contentLabel,
            ContentDescription: '',
            ContentCreatorName: contentCreatorName,
            ContentDate: getDICOMDate(),
            ContentTime: getDICOMTime(),
            FrameOfReferenceUID: primaryImage.frameOfReferenceUid,
            ReferencedSeriesSequence: [
                buildReferencedSeriesItem(primaryImage),
                buildReferencedSeriesItem(secondaryImage),
            ],
            RegistrationSequence: [
                {
                    FrameOfReferenceUID: primaryImage.frameOfReferenceUid,
                    MatrixRegistrationSequence: {
                        MatrixSequence: {
                            FrameOfReferenceTransformationMatrixType: 'RIGID',
                            FrameOfReferenceTransformationMatrix: [
                                1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1,
                            ],
                        },
                    },
                },
                {
                    FrameOfReferenceUID: secondaryImage.frameOfReferenceUid,
                    MatrixRegistrationSequence: {
                        MatrixSequence: {
                            FrameOfReferenceTransformationMatrixType: 'RIGID',
                            FrameOfReferenceTransformationMatrix:
                                mat4ToRowMajorArray(transformation),
                        },
                    },
                },
            ],
        };

        const dataset = DicomProcessor.createDcmjsDataset(
            patient,
            primaryImage,
            Modality.REG,
            StorageSOPClassUID.SpatialRegistrationStorage,
            sopInstanceUid,
            seriesInstanceUid,
            registrationSpecificFields
        );

        return new DicomRigidRegistration(
            sopInstanceUid,
            primaryImage.frameOfReferenceUid,
            secondaryImage.frameOfReferenceUid,
            contentLabel,
            transformation,
            dataset
        );
    }

    /**
     * Creates a DCMJS DICOM dataset using the provided patient, image, modality, and other metadata.
     *
     * This method generates a normalized DICOM dataset inside a DcmJsDatasetWrapper by combining information
     * from the provided metadata. The resulting dataset can optionally include additional fields from the
     * `appendDataset` parameter.
     *
     * @param patient - The patient object containing patient-related DICOM metadata.
     * @param image - The 3D image object containing the first slice's DICOM dataset.
     * @param modality - The modality of the DICOM dataset (e.g., CT, MR, etc.).
     * @param storageSopClass - The SOP Class UID for the storage of the DICOM dataset.
     * @param sopInstanceUid - The unique identifier for the SOP instance.
     * @param seriesInstanceUid - The unique identifier for the series instance.
     * @param appendDataset - Optional additional fields to append to the normalized dataset.
     * @returns A `DcmJsDatasetWrapper` instance containing the constructed DICOM dataset.
     * @throws {Error} If the patient ID in the image does not match the patient ID in the provided patient dataset.
     */
    private static createDcmjsDataset(
        patient: Patient,
        image: Image3D,
        modality: Modality,
        storageSopClass: StorageSOPClassUID,
        sopInstanceUid: string,
        seriesInstanceUid: string,
        appendDataset?: unknown
    ): DcmJsDatasetWrapper {
        const imageDicomDataset = image.firstSlice.datasetWrapper
            .dataset as any;

        if (imageDicomDataset.PatientID !== patient.dataSet.PatientID) {
            throw new Error(
                `Patient ID mismatch between image (${imageDicomDataset.PatientID}) and patient (${patient.dataSet.PatientID})`
            );
        }

        // try to get the original patient name as a PN (person name) object from original DICOM dataset,
        // construct the alphabetic representation from dataset's PatientName prop if not available
        const patientName = imageDicomDataset.PatientName || [
            { Alphabetic: patient.dataSet.PatientName },
        ];

        let normalizedDataset: unknown = {
            PatientID: patient.dataSet.PatientID,
            PatientName: patientName,
            PatientBirthDate: imageDicomDataset.PatientBirthDate,
            PatientSex: imageDicomDataset.PatientSex,
            StudyInstanceUID: image.studyUid,
            StudyDate: imageDicomDataset.StudyDate,
            StudyTime: imageDicomDataset.StudyTime,
            ReferringPhysicianName: imageDicomDataset.ReferringPhysicianName,
            StudyID: imageDicomDataset.StudyID,
            AccessionNumber: imageDicomDataset.AccessionNumber,
            Modality: modality,
            OperatorsName: '', // https://dicom.innolitics.com/ciods/rt-structure-set/rt-series/00081070

            Manufacturer: AppConfig.dicomManufacturer,
            ManufacturerModelName: AppConfig.dicomManufacturerModelName,
            SoftwareVersions: AppConfig.version,

            ApprovalStatus: 'UNAPPROVED',

            SeriesInstanceUID: seriesInstanceUid,
            SeriesNumber: image.seriesMetadata.seriesNumber,
            InstanceNumber: '1',

            SOPClassUID: storageSopClass,
            SOPInstanceUID: sopInstanceUid,

            _meta: {
                FileMetaInformationVersion: {
                    Value: [{ '0': 0, '1': 1 }],
                    vr: 'OB',
                },
                ImplementationClassUID: {
                    Value: [AppConfig.implementationClassUID],
                    vr: 'UI',
                },
                ImplementationVersionName: {
                    Value: [AppConfig.dicomManufacturer],
                    vr: 'SH',
                },
                MediaStorageSOPClassUID: {
                    Value: [storageSopClass],
                    vr: 'UI',
                },
                MediaStorageSOPInstanceUID: {
                    Value: [sopInstanceUid],
                    vr: 'UI',
                },
                TransferSyntaxUID: {
                    Value: [TransferSyntaxUID.ImplicitVRLittleEndian],
                    vr: 'UI',
                },
            },
        };

        if (appendDataset) {
            normalizedDataset = Object.assign(
                {},
                normalizedDataset,
                appendDataset
            );
        }

        const dataset = new DcmJsDatasetWrapper(normalizedDataset);

        return dataset;
    }

    private storeImageSlice(imageDataset: any): DicomImageSlice | null {
        const imageSlice = dcmjsToImageSlice(imageDataset);

        if (imageSlice === null) {
            console.error(
                `Attempt to load ImageSlice ${imageDataset.SOPInstanceUID} from DICOM datasource failed.`
            );
            return null;
        }
        this.storeSeriesObject(imageDataset, imageSlice);
        return imageSlice;
    }

    private storeStructureSet(dataset: any): DicomStructureSet | null {
        const structureSet = dcmjsToStructureSet(dataset);

        if (structureSet === null) {
            console.error(
                `Attempt to load StructureSet ${dataset.SOPInstanceUID} from DICOM datasource failed.`
            );
            return null;
        }
        this.storeSeriesObject(dataset, structureSet);
        return structureSet;
    }

    private storeDose(dataset: any): DicomDose | null {
        const dose = dcmjsToDose(dataset);

        if (dose === null) {
            console.error(
                `Attempt to load Dose ${dataset.SOPInstanceUID} from DICOM datasource failed.`
            );
            return null;
        }
        this.storeSeriesObject(dataset, dose);
        return dose;
    }

    private storePlan(dataset: any): DicomPlan | null {
        const plan = dcmjsToPlan(dataset);

        if (plan === null) {
            console.error(
                `Attempt to load Plan ${dataset.SOPInstanceUID} from DICOM datasource failed.`
            );
            return null;
        }
        this.storeSeriesObject(dataset, plan);
        return plan;
    }

    private storeRegistration(dataset: any): DicomRegistration | null {
        const registration = dcmjsToRegistration(dataset);

        if (registration === null) {
            console.error(
                `Attempt to load Registration ${dataset.SOPInstanceUID} from DICOM datasource failed.`
            );
            return null;
        }
        this.storeSeriesObject(dataset, registration);
        return registration;
    }

    /**
     * Updates DicomProcessor's internal patient dataset with given
     * DICOM dataset and corresponding domain object. This is for
     * use cases where these objects are created outside of
     * DicomProcessor.
     *
     * @param dataset - The DICOM dataset to be stored.
     * @param dicomBase - Matching DICOM domain object.
     */
    public updatePatientDataset(dataset: any, dicomBase: DicomBase) {
        this.storeSeriesObject(dataset, dicomBase);
    }

    private storeSeriesObject(dataset: any, dicomBase: DicomBase) {
        const patient = this.getOrCreatePatient(
            dataset.PatientID,
            dataset.PatientName[0]?.Alphabetic,
            dataset.PatientSex,
            dataset.PatientBirthDate
        );
        const series = this.getOrCreateSeries(dataset, patient);
        series.addSeriesObject(dicomBase);
    }

    private getOrCreatePatient(
        patientId: string,
        patientName: string,
        patientSex?: string,
        patientBirthDate?: string
    ): PatientDataSet {
        const patient = this._patients.get(patientId);
        if (!patient) {
            const pds = new PatientDataSet({
                patientName: patientName,
                patientId: patientId,
                patientSex: patientSex,
                patientBirthDate: patientBirthDate,
            } as DicomPatient);
            this._patients.set(patientId, pds);
            return pds;
        }
        return patient;
    }

    private findSeries(
        patient: PatientDataSet,
        seriesInstanceUID: string
    ): Series | undefined {
        return patient.series.find(
            (ser) => ser.seriesInstanceUID === seriesInstanceUID
        );
    }

    private getOrCreateSeries(dataset: any, patient: PatientDataSet): Series {
        const series = this.findSeries(
            patient,
            dataset.SeriesInstanceUID as string
        );
        if (series === undefined) {
            const dicomSeriesModule: DicomSeries = {
                modality: strToModality(dataset.Modality),
                seriesInstanceUID: dataset.SeriesInstanceUID,
                seriesDescription: dataset.SeriesDescription ?? '',
                seriesNumber: dataset.SeriesNumber,
                seriesDate: formatDate(dataset.SeriesDate),
                frameOfReferenceUid: dataset.FrameOfReferenceUID,
            };
            const ser = new Series(dicomSeriesModule);
            patient.series.push(ser);
            return ser;
        }
        return series;
    }

    private readonly _patients = new Map<string, PatientDataSet>();

    public get patients(): PatientDataSet[] {
        return Array.from(this._patients.values());
    }

    /** Returns the DICOM PatientIDs of all patients currently held by this processor. */
    public getPatientIds(): string[] {
        return Array.from(this._patients.keys());
    }

    public removeProcessedSopUids(sopUids: string[]): void {
        for (const uid of sopUids) {
            this.processedSopUids.delete(uid);
        }
    }

    /**
     * Clears all internal state (processed SOP UIDs and patient data)
     * so that new data can be imported without conflicts from a
     * previous patient session.
     */
    public clearProcessedState(): void {
        this.processedSopUids.clear();
        this._patients.clear();
    }

    /**
     * Removes a single patient's data from the processor,
     * including any SOP UIDs that were marked as processed
     * for that patient's series. Used when switching patients
     * — the old patient's data must be dropped while keeping
     * the incoming patient's data intact for committing via
     * toRepository().
     */
    public removePatientData(patientId: string): boolean {
        const patientData = this._patients.get(patientId);
        if (!patientData) {
            return false;
        }

        // Collect all SOP UIDs belonging to this patient
        // so they can be removed from processedSopUids.
        const sopUidsToRemove: string[] = [];
        for (const series of patientData.series) {
            for (const obj of series.seriesObjects) {
                sopUidsToRemove.push(obj.sopInstanceUid);
            }
        }

        this.removeProcessedSopUids(sopUidsToRemove);
        return this._patients.delete(patientId);
    }

    /**
     * Fully removes a single SOP object from the processor: deletes it from
     * the internal patient data and un-marks its SOP UID as processed.
     * If the containing series becomes empty after removal, the series is also removed.
     */
    public removeSopObjectFromPatient(
        patientId: string,
        sopInstanceUid: string
    ): boolean {
        const patientDataSet = this._patients.get(patientId);
        if (!patientDataSet) {
            return false;
        }

        for (const series of patientDataSet.series) {
            if (series.removeSeriesObject(sopInstanceUid)) {
                this.processedSopUids.delete(sopInstanceUid);
                if (series.numberOfObjects === 0) {
                    patientDataSet.removeSeries(series.seriesInstanceUID);
                }
                return true;
            }
        }

        return false;
    }

    public removeSeriesFromPatient(
        patientId: string,
        seriesInstanceUID: string
    ): boolean {
        const patientDataSet = this._patients.get(patientId);
        if (!patientDataSet) {
            return false;
        }

        const series = this.findSeries(patientDataSet, seriesInstanceUID);
        if (!series) {
            return false;
        }

        // Collect SOP UIDs from the series objects so they can be
        // re-imported after deletion
        const sopUids = series.seriesObjects.map((obj) => obj.sopInstanceUid);
        this.removeProcessedSopUids(sopUids);

        return patientDataSet.removeSeries(seriesInstanceUID);
    }
}

function dcmjsToImageSlice(dataset: any): DicomImageSlice {
    CheckMandatory(dataset, 'SOPInstanceUID');
    CheckMandatory(dataset, 'SeriesInstanceUID');
    CheckMandatory(dataset, 'FrameOfReferenceUID');

    const directionCosineFirstRow = vec3.fromValues(
        parseFloat(dataset.ImageOrientationPatient[0]),
        parseFloat(dataset.ImageOrientationPatient[1]),
        parseFloat(dataset.ImageOrientationPatient[2])
    );
    const directionCosineFirstCol = vec3.fromValues(
        parseFloat(dataset.ImageOrientationPatient[3]),
        parseFloat(dataset.ImageOrientationPatient[4]),
        parseFloat(dataset.ImageOrientationPatient[5])
    );
    const directionCosineNormal = vec3.create();
    vec3.cross(
        directionCosineNormal,
        directionCosineFirstRow,
        directionCosineFirstCol
    );

    const rescaleIntercept = parseFloat(dataset.RescaleIntercept) || 0.0;
    const rescaleSlope = parseFloat(dataset.RescaleSlope) || 1.0;

    const headFirstProne = dataset.PatientPosition === 'HFP';
    const ORIENTATION_TOLERANCE = 1e-4;
    const tilted = !dataset.ImageOrientationPatient.map((value: any) =>
        parseFloat(value)
    ).every(
        (value: number) =>
            Math.abs(value) < ORIENTATION_TOLERANCE ||
            Math.abs(Math.abs(value) - 1.0) < ORIENTATION_TOLERANCE
    );
    const mrSpecificaAttrs = getMRModalitySpecificAttributes(dataset);
    const ctSpecificAttrs = getCTModalitySpecificAttributes(dataset);
    const petSpecificAttrs = getPetSpecificAttributes(dataset);

    const patientWeightKg = parseFloat(dataset.PatientWeight) || undefined;
    const seriesTime = dataset.SeriesTime ?? undefined;
    const acquisitionTime = dataset.AcquisitionTime ?? undefined;
    const studyDate = dataset.StudyDate ?? undefined;
    const seriesDate = dataset.SeriesDate ?? undefined;
    const acquisitionDate = dataset.AcquisitionDate ?? undefined;

    const pixelSpacing = vec2.fromValues(
        parseFloat(dataset.PixelSpacing[0]),
        parseFloat(dataset.PixelSpacing[1])
    );

    // only uniform pixel spacing is supported
    CheckAndThrow(
        Math.abs(pixelSpacing[0] - pixelSpacing[1]) < 1e-2,
        `Non-uniform pixel spacing: ${pixelSpacing[0].toFixed(
            3
        )}, ${pixelSpacing[1].toFixed(3)}`,
        'PixelSpacing'
    );

    const imageSlice = new DicomImageSlice(
        dataset.SOPInstanceUID,
        dataset.Modality,
        pixelSpacing,
        vec3.fromValues(
            parseFloat(dataset.ImagePositionPatient[0]),
            parseFloat(dataset.ImagePositionPatient[1]),
            parseFloat(dataset.ImagePositionPatient[2])
        ),
        mat3.fromValues(
            directionCosineFirstRow[0],
            directionCosineFirstRow[1],
            directionCosineFirstRow[2],
            directionCosineFirstCol[0],
            directionCosineFirstCol[1],
            directionCosineFirstCol[2],
            directionCosineNormal[0],
            directionCosineNormal[1],
            directionCosineNormal[2]
        ),
        parseFloat(dataset.SliceThickness),
        dataset.SeriesDescription ?? '',
        dataset.SeriesInstanceUID,
        dataset.FrameOfReferenceUID,
        dataset.Manufacturer ?? '',
        dataset.ManufacturerModelName ?? '',
        dataset.InstanceNumber,
        dataset.ImageType ?? ['UNKNOWN', 'UNKNOWN'],
        dataset.Rows,
        dataset.Columns,
        dataset.PatientPosition ?? '',
        dataset.StationName ?? '',
        dataset.FilterType ?? '',
        rescaleIntercept,
        rescaleSlope,
        headFirstProne,
        tilted,
        new DcmJsDatasetWrapper(dataset),
        patientWeightKg,
        seriesTime,
        acquisitionTime,
        studyDate,
        seriesDate,
        acquisitionDate,
        ctSpecificAttrs.kvp,
        ctSpecificAttrs.ctdi,
        ctSpecificAttrs.kernel,
        ctSpecificAttrs.protocolName,
        mrSpecificaAttrs.scanningSequence,
        mrSpecificaAttrs.mraAcquisitionType,
        mrSpecificaAttrs.sequenceVariant,
        petSpecificAttrs?.radionuclideTotalDose,
        petSpecificAttrs?.radionuclideHalfLife,
        petSpecificAttrs?.radiopharmaceuticalStartTime,
        petSpecificAttrs?.radiopharmaceuticalStartDateTime
    );

    imageSlice.windowLevel = dcmjsGetWindowLevel(dataset);

    return imageSlice;
}

function dcmjsToDose(dataset: any): DicomDose {
    CheckAndThrow(
        dataset.PixelRepresentation === 0,
        `Unsupported PixelRepresentation '${dataset.PixelRepresentation}'. SOP UID ${dataset.SOPInstanceUID}`,
        'PixelRepresentation'
    );
    CheckAndThrow(
        dataset.BitsAllocated === 16 || dataset.BitsAllocated === 32,
        `Unsupported BitsAllocated ${dataset.BitsAllocated}. SOP UID ${dataset.SOPInstanceUID}`,
        'BitsAllocated'
    );
    CheckAndThrow(
        dataset.HighBit === dataset.BitsStored - 1,
        `Unsupported HighBit '${dataset.HighBit}'. SOP UID ${dataset.SOPInstanceUID}`,
        'HighBit'
    );

    const directionCosineFirstRow = vec3.fromValues(
        parseFloat(dataset.ImageOrientationPatient[0]),
        parseFloat(dataset.ImageOrientationPatient[1]),
        parseFloat(dataset.ImageOrientationPatient[2])
    );
    const directionCosineFirstCol = vec3.fromValues(
        parseFloat(dataset.ImageOrientationPatient[3]),
        parseFloat(dataset.ImageOrientationPatient[4]),
        parseFloat(dataset.ImageOrientationPatient[5])
    );
    const directionCosineNormal = vec3.create();
    vec3.cross(
        directionCosineNormal,
        directionCosineFirstRow,
        directionCosineFirstCol
    );

    const rtplanReference = dataset.ReferencedRTPlanSequence
        ? dataset.ReferencedRTPlanSequence[0].ReferencedSOPInstanceUID
        : '';

    const referencedRtStructureSetUid = dataset.ReferencedStructureSetSequence
        ? dataset.ReferencedStructureSetSequence[0].ReferencedSOPInstanceUID
        : '';

    const sliceResolution = getSliceResolution(dataset.GridFrameOffsetVector);

    // DICOM GridFrameOffsetVector may have a negative step when frames
    // extend opposite to the cross-product of row/col direction cosines.
    // Normalize to a positive-step grid by adjusting the origin to the
    // last frame position; the frame order in pixel data is reversed
    // lazily in DicomDose.toInternalPixelData() so the original DICOM
    // dataset stays unmodified for export.
    const gfov: number[] = dataset.GridFrameOffsetVector;
    let reverseFrameOrder = false;
    if (gfov.length >= 2) {
        const gridFrameStep = gfov[1] - gfov[0];
        reverseFrameOrder = gridFrameStep < 0;
    }
    let imagePosition = vec3.fromValues(
        parseFloat(dataset.ImagePositionPatient[0]),
        parseFloat(dataset.ImagePositionPatient[1]),
        parseFloat(dataset.ImagePositionPatient[2])
    );
    if (reverseFrameOrder) {
        const lastOffset = gfov[gfov.length - 1];
        imagePosition = vec3.scaleAndAdd(
            vec3.create(),
            imagePosition,
            directionCosineNormal,
            lastOffset
        );
    }

    const dicomDvhs = importDvhSequence(dataset.DVHSequence);

    const dose = new DicomDose(
        dataset.SOPInstanceUID,
        dataset.FrameOfReferenceUID,
        vec2.fromValues(
            parseFloat(dataset.PixelSpacing[0]),
            parseFloat(dataset.PixelSpacing[1])
        ),
        sliceResolution,
        imagePosition,
        mat3.fromValues(
            directionCosineFirstRow[0],
            directionCosineFirstRow[1],
            directionCosineFirstRow[2],
            directionCosineFirstCol[0],
            directionCosineFirstCol[1],
            directionCosineFirstCol[2],
            directionCosineNormal[0],
            directionCosineNormal[1],
            directionCosineNormal[2]
        ),
        dataset.SeriesDescription,
        dataset.Manufacturer,
        dataset.ManufacturerModelName,
        dataset.Rows,
        dataset.Columns,
        dataset.NumberOfFrames,
        strToDoseUnit(dataset.DoseUnits),
        strToDoseType(dataset.DoseType),
        strToDoseSummationType(dataset.DoseSummationType),
        dataset.DoseComment,
        dataset.DoseGridScaling,
        dataset.SoftwareVersions,
        rtplanReference,
        referencedRtStructureSetUid,
        dicomDvhs,
        new DcmJsDatasetWrapper(dataset),
        reverseFrameOrder,
        dataset.SeriesDate,
        dataset.SeriesTime
    );

    return dose;
}

/**
 *
 * @param dataset
 * TODO: Currently only supports single registration in a single file. Technically there could be also more than that.
 */
function dcmjsToRegistration(dataset: any): DicomRegistration {
    if (dataset.DeformableRegistrationSequence) {
        throw new Error(
            `Attempt to read DeformableRegistration from dcmjs dataset. Deformable registrations are currently not supported.`
        );
    } else if (dataset.RegistrationSequence) {
        CheckAndThrow(
            dataset.RegistrationSequence.length > 0,
            `Unsupported empty RegistrationSequence. SOP UID ${dataset.SOPInstanceUID}`,
            `RegistrationSequence`
        );
        const sourceFrameOfReferenceUid: string = dataset.FrameOfReferenceUID;
        for (let i = 0; i < dataset.RegistrationSequence.length; ++i) {
            const matrixSequence =
                dataset.RegistrationSequence[i].MatrixRegistrationSequence
                    .MatrixSequence;
            const array = matrixSequence.FrameOfReferenceTransformationMatrix;
            CheckAndThrow(
                array,
                `Rigid registration MatrixSequence missing FrameOfReferenceTransformationMatrix. SOP UID ${dataset.SOPInstanceUID}`,
                `FrameOfReferenceTransformationMatrix`
            );
            CheckAndThrow(
                array.length === 16,
                `Rigid registration FrameOfReferenceTransformationMatrix has a matrix of length ${array.length}. SOP UID ${dataset.SOPInstanceUID}`,
                `FrameOfReferenceTransformationMatrix`
            );
            const transformationMatrix = mat4.fromValues(
                array[0],
                array[4],
                array[8],
                array[12],
                array[1],
                array[5],
                array[9],
                array[13],
                array[2],
                array[6],
                array[10],
                array[14],
                array[3],
                array[7],
                array[11],
                array[15]
            );
            if (!mat4.equals(transformationMatrix, mat4.create())) {
                return new DicomRigidRegistration(
                    dataset.SOPInstanceUID,
                    sourceFrameOfReferenceUid,
                    dataset.RegistrationSequence[i].FrameOfReferenceUID,
                    dataset.ContentLabel,
                    transformationMatrix,
                    new DcmJsDatasetWrapper(dataset)
                );
            }
        }
        throw new Error(
            `Attempt to decode registration, but all the registrations were identity`
        );
    } else {
        throw new Error(
            `Attempt to decode registration that does not contain RegistrationSequence.`
        );
    }
}

/**
 * Convert raw DICOM 16-bit image slice data to an array.
 * Applies rescale slope/intercept transformation: output = (raw & mask) * slope + intercept
 *
 * Optimized to use direct loop instead of Array.from() with callback for better performance.
 * For a 528x528 slice, this processes ~279K pixels.
 */
export function dicomSlicePixelDataToInternal(
    pixelRepresentation: number,
    arraybuf: ArrayBuffer,
    bitsAllocated: number,
    bitsStored: number,
    slope: number,
    intercept: number
): Int16Array {
    if (bitsAllocated > 16) {
        throw new Error(
            `dicomSlicePixelDataToInternal does not support ${bitsAllocated}-bit data. `
        );
    }

    const sourceArray = bufferToArray(
        arraybuf,
        pixelRepresentation,
        bitsAllocated
    );

    // Fast path: no transformation needed and no bit masking required
    const needsBitMask = bitsStored && bitsStored < bitsAllocated;
    if ((!slope || slope === 1) && intercept === 0 && !needsBitMask) {
        return sourceArray instanceof Int16Array
            ? sourceArray
            : new Int16Array(sourceArray);
    }

    // Calculate bit mask for stored bits
    let mask = bitsStored ? -1 >>> (32 - bitsStored) : 0xffff;
    if (pixelRepresentation === 1 && bitsStored < 16) {
        // Hitachi CT images had an example where the pixel data was 13 bits
        // stored in 16 bits with high bit still being the bit 16
        mask = mask << (16 - bitsStored);
    }

    const len = sourceArray.length;
    const result = new Int16Array(len);

    // Optimized path: slope=1, just add intercept (common for CT)
    if (slope === 1) {
        for (let i = 0; i < len; i++) {
            result[i] = (sourceArray[i] & mask) + intercept;
        }
        return result;
    }

    // General case: apply full transformation
    const effectiveSlope = slope || 1;
    for (let i = 0; i < len; i++) {
        result[i] = (sourceArray[i] & mask) * effectiveSlope + intercept;
    }
    return result;
}

/**
 * Convert raw DICOM 16-bit image slice data to a Float32Array.
 * Applies rescale slope/intercept transformation without Int16 truncation.
 * Use this for modalities (e.g. PET) where rescaled values can exceed the
 * Int16 range (−32768 to 32767).
 *
 * output[i] = (raw[i] & mask) * slope + intercept
 */
export function dicomSlicePixelDataToFloat32(
    pixelRepresentation: number,
    arraybuf: ArrayBuffer,
    bitsAllocated: number,
    bitsStored: number,
    slope: number,
    intercept: number
): Float32Array {
    if (bitsAllocated > 16) {
        throw new Error(
            `dicomSlicePixelDataToFloat32 does not support ` +
                `${bitsAllocated}-bit data.`
        );
    }

    const sourceArray = bufferToArray(
        arraybuf,
        pixelRepresentation,
        bitsAllocated
    );

    const needsBitMask = bitsStored && bitsStored < bitsAllocated;
    const len = sourceArray.length;
    const result = new Float32Array(len);

    // Fast path: no transformation needed and no bit masking required
    if ((!slope || slope === 1) && intercept === 0 && !needsBitMask) {
        for (let i = 0; i < len; i++) {
            result[i] = sourceArray[i];
        }
        return result;
    }

    // Calculate bit mask for stored bits
    let mask = bitsStored ? -1 >>> (32 - bitsStored) : 0xffff;
    if (pixelRepresentation === 1 && bitsStored < 16) {
        mask = mask << (16 - bitsStored);
    }

    // Optimized path: slope=1, just add intercept
    if (slope === 1) {
        for (let i = 0; i < len; i++) {
            result[i] = (sourceArray[i] & mask) + intercept;
        }
        return result;
    }

    // General case: apply full transformation
    const effectiveSlope = slope || 1;
    for (let i = 0; i < len; i++) {
        result[i] = (sourceArray[i] & mask) * effectiveSlope + intercept;
    }
    return result;
}

/**
 * Convert raw DICOM dose pixel data from a dataset wrapper to an
 * internal Uint32Array. Handles the dcmjs PixelData array-wrapping
 * quirk and applies a bit mask when BitsStored < BitsAllocated.
 */
export function dicomDosePixelDataToInternal(
    datasetWrapper: DcmJsDatasetWrapper
): Uint32Array {
    const dataset = datasetWrapper.dataset as any;

    const pixelRepresentation: number = dataset.PixelRepresentation;
    if (pixelRepresentation !== 0) {
        throw new Error(
            'dicomDosePixelDataToInternal does not support ' +
                `signed data (pixelRepresentation=${pixelRepresentation}).`
        );
    }

    // dcmjs 0.19.6 has a bug where naturalized array buffers
    // (such as PixelData) are unnecessarily put inside an array.
    // TODO: revert this workaround once dcmjs has been patched
    const arraybuf: ArrayBuffer = Object.prototype.hasOwnProperty.call(
        dataset.PixelData,
        'length'
    )
        ? dataset.PixelData[0]
        : dataset.PixelData;

    const bitsAllocated: number = dataset.BitsAllocated;
    const bitsStored: number = dataset.BitsStored;

    const sourceArray = bufferToArray(
        arraybuf,
        pixelRepresentation,
        bitsAllocated
    );

    // Fast path: if all bits are stored, no masking needed
    if (bitsStored === bitsAllocated) {
        return sourceArray instanceof Uint32Array
            ? sourceArray
            : new Uint32Array(sourceArray);
    }

    const mask = -1 >>> (32 - bitsStored);
    const len = sourceArray.length;
    const result = new Uint32Array(len);
    for (let i = 0; i < len; i++) {
        result[i] = sourceArray[i] & mask;
    }
    return result;
}

function bufferToArray(
    arraybuf: ArrayBuffer,
    pixelRepresentation: number,
    bitsAllocated: number
): Uint16Array | Int16Array | Uint32Array {
    if (bitsAllocated === 16) {
        if (pixelRepresentation === 0) {
            return new Uint16Array(arraybuf);
        } else {
            return new Int16Array(arraybuf);
        }
    }

    if (bitsAllocated === 32) {
        if (pixelRepresentation !== 0) {
            throw new Error(
                `Unexpected pixelRepresentation ${pixelRepresentation} with ${bitsAllocated}-bit data`
            );
        }
        return new Uint32Array(arraybuf);
    }

    throw new Error(
        `Could not initialize byte buffer, pixelRepresentation ${pixelRepresentation}, bitsAllocated ${bitsAllocated}`
    );
}

export function dcmjsToStructureSet(dataset: any): DicomStructureSet | null {
    if (
        !dataset.ReferencedFrameOfReferenceSequence ||
        !dataset.ReferencedFrameOfReferenceSequence.RTReferencedStudySequence ||
        !dataset.ReferencedFrameOfReferenceSequence.RTReferencedStudySequence
            .RTReferencedSeriesSequence ||
        !dataset.ReferencedFrameOfReferenceSequence.RTReferencedStudySequence
            .RTReferencedSeriesSequence.SeriesInstanceUID
    ) {
        return null;
    }

    const refSeriesInstanceUID =
        dataset.ReferencedFrameOfReferenceSequence.RTReferencedStudySequence.RTReferencedSeriesSequence.SeriesInstanceUID.substring(
            0,
            64
        );
    const refSeriesSOPClassUID =
        dataset.ReferencedFrameOfReferenceSequence.RTReferencedStudySequence
            .RTReferencedSeriesSequence?.ContourImageSequence[0]
            ?.ReferencedSOPClassUID ?? StorageSOPClassUID.CTImageStorage;

    const frameOfReferenceUid =
        dataset.ReferencedFrameOfReferenceSequence.FrameOfReferenceUID;
    const sopInstanceUID = dataset.SOPInstanceUID;

    const datasetWrapper = new DcmJsDatasetWrapper(dataset);
    const referencedSopUids = new Array<string>();
    const rois = parseRois(datasetWrapper, referencedSopUids);
    getReferencedSopUidsFromReferencedFrameOfReference(
        datasetWrapper,
        referencedSopUids
    );

    const ss = new DicomStructureSet(
        datasetWrapper,
        sopInstanceUID,
        frameOfReferenceUid,
        refSeriesInstanceUID,
        rois,
        referencedSopUids,
        refSeriesSOPClassUID,
        dataset.SeriesInstanceUID
    );

    return ss;
}

function parseRois(
    datasetWrapper: DcmJsDatasetWrapper,
    referencedSopUids: Array<string>
): Map<number, DicomRoi> {
    const dataset = datasetWrapper.dataset as any;
    const rois = new Map<number, DicomRoi>();
    const structureSetROISequence = Array.isArray(
        dataset.StructureSetROISequence
    )
        ? dataset.StructureSetROISequence
        : [dataset.StructureSetROISequence];
    structureSetROISequence.forEach(
        (roi: { ROINumber: number; ROIName: string }) => {
            const roiNumber = roi.ROINumber;
            const name = roi.ROIName;
            rois.set(roiNumber, new DicomRoi(datasetWrapper, roiNumber, name));
        }
    );
    const roiObservationsSequence = Array.isArray(
        dataset.RTROIObservationsSequence
    )
        ? dataset.RTROIObservationsSequence
        : [dataset.RTROIObservationsSequence];
    roiObservationsSequence.forEach(
        (refRoi: {
            ReferencedROINumber: number;
            RTROIInterpretedType: string;
        }) => {
            if (refRoi) {
                const roi = rois.get(refRoi.ReferencedROINumber);
                if (!roi) {
                    console.warn(
                        `StructureSet ROIObservationSequence contains roiNumber ${refRoi.ReferencedROINumber}, but that ROINumber is not included in the structureSetROISequence`
                    );
                }
            } else {
                console.warn(
                    `StructureSet ROIObservationSequence contains empty value`
                );
            }
        }
    );

    dataset.ROIContourSequence = Array.isArray(dataset.ROIContourSequence)
        ? dataset.ROIContourSequence
        : [dataset.ROIContourSequence];
    dataset.ROIContourSequence.forEach((roiContour: any) => {
        const roi = rois.get(roiContour.ReferencedROINumber);
        if (roi) {
            roi.contours = [];
            const color = roiContour.ROIDisplayColor
                ? roiContour.ROIDisplayColor.map((x: any) => parseInt(x))
                : generateRandomRgbColor();
            roi.color = { R: color[0], G: color[1], B: color[2] };
            rois.set(roiContour.ReferencedROINumber, roi);

            roiContour.ContourSequence = Array.isArray(
                roiContour.ContourSequence
            )
                ? roiContour.ContourSequence
                : [roiContour.ContourSequence];

            // Workaround for dmjs bug: if ContourSequence contains undefined values only, serialization back to DICOM will fail later
            if (
                roiContour.ContourSequence.length > 0 &&
                roiContour.ContourSequence.every(
                    (x: unknown) => x === undefined
                )
            ) {
                roiContour.ContourSequence = [];
            }

            for (const contour of roiContour.ContourSequence) {
                if (!contour) {
                    continue;
                }

                if (
                    contour.ContourGeometricType !== 'CLOSED_PLANAR' &&
                    contour.ContourGeometricType !== 'POINT'
                ) {
                    console.error(
                        `Unsupported contour type ${contour.ContourGeometricType}`
                    );
                    continue;
                }

                // check if there is a referenced image
                const contourImageSequenceItem =
                    contour.ContourImageSequence &&
                    contour.ContourImageSequence.length > 0
                        ? contour.ContourImageSequence[0]
                        : null;

                if (
                    contourImageSequenceItem?.ReferencedSOPInstanceUID &&
                    !referencedSopUids.includes(
                        contourImageSequenceItem.ReferencedSOPInstanceUID
                    )
                ) {
                    referencedSopUids.push(
                        contourImageSequenceItem.ReferencedSOPInstanceUID
                    );
                }

                const nrPts = parseInt(contour.NumberOfContourPoints);
                // dcmjs may return ContourData as a single string
                // for small value sets (e.g. POINT contours with 3
                // values) instead of an array. Normalize to array.
                let data = contour.ContourData;
                if (typeof data === 'string') {
                    data = data.split('\\');
                } else if (!Array.isArray(data)) {
                    throw new Error(
                        `Invalid ContourData type in RTSTRUCT: expected string or array but got ${typeof data}`
                    );
                }

                CheckAndThrow(
                    nrPts * 3 === data.length,
                    `Unexpected number of contour points ${nrPts} while ContourData length is ${data.length}`,
                    'NumberOfContourPoints'
                );

                const points: vec3[] = [];
                for (let i = 0; i < nrPts; ++i) {
                    const x = parseFloat(data[i * 3]);
                    const y = parseFloat(data[i * 3 + 1]);
                    const z = parseFloat(data[i * 3 + 2]);
                    points.push(vec3.fromValues(x, y, z));
                }

                if (points.length > 0) {
                    roi.contours.push(
                        new DicomContour(
                            contour.ContourGeometricType,
                            points,
                            contourImageSequenceItem?.ReferencedSOPClassUID,
                            contourImageSequenceItem?.ReferencedSOPInstanceUID
                        )
                    );
                }
            }
        } else {
            console.warn();
        }
    });
    return rois;
}

function getReferencedSopUidsFromReferencedFrameOfReference(
    datasetWrapper: DcmJsDatasetWrapper,
    referencedSopUids: Array<string>
) {
    const dataset = datasetWrapper.dataset as any;

    if (Array.isArray(dataset.ReferencedFrameOfReferenceSequence)) {
        for (const frameOfRef of dataset.ReferencedFrameOfReferenceSequence) {
            if (Array.isArray(frameOfRef.RTReferencedStudySequence)) {
                for (const rtRefStudy of frameOfRef.RTReferencedStudySequence) {
                    if (Array.isArray(rtRefStudy.RTReferencedSeriesSequence)) {
                        for (const rtRefSeries of rtRefStudy.RTReferencedSeriesSequence) {
                            if (
                                Array.isArray(rtRefSeries.ContourImageSequence)
                            ) {
                                for (const contourImage of rtRefSeries.ContourImageSequence) {
                                    const sopInstanceUid =
                                        contourImage.ReferencedSOPInstanceUID;
                                    if (
                                        sopInstanceUid &&
                                        !referencedSopUids.includes(
                                            sopInstanceUid
                                        )
                                    ) {
                                        referencedSopUids.push(sopInstanceUid);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

export function getMRModalitySpecificAttributes(dataset: any) {
    const scanningSequence = dataset.ScanningSequence ?? undefined;
    const mraAcquisitionType = dataset.MRAcquisitionType ?? undefined;
    const sequenceVariant = dataset.SequenceVariant ?? undefined;
    return { scanningSequence, mraAcquisitionType, sequenceVariant };
}

export function getCTModalitySpecificAttributes(dataset: any) {
    const kvp = dataset.KVP ? `${dataset.KVP} kV` : undefined;
    const ctdi = dataset.CTDIvol
        ? `${dataset.CTDIvol.toFixed(2)} mGy`
        : undefined;
    const kernel = Array.isArray(dataset.ConvolutionKernel)
        ? dataset.ConvolutionKernel.join(', ')
        : typeof dataset.ConvolutionKernel === 'string'
          ? dataset.ConvolutionKernel
          : undefined;
    const protocolName = dataset.ProtocolName ?? undefined;
    return { kvp, ctdi, kernel, protocolName };
}

export function getPetSpecificAttributes(dataset: any) {
    const radioPharmSeq = dataset.RadiopharmaceuticalInformationSequence;
    if (!radioPharmSeq) {
        return undefined;
    }

    const radioPharmItem = Array.isArray(radioPharmSeq)
        ? radioPharmSeq[0]
        : radioPharmSeq;

    if (!radioPharmItem) {
        return undefined;
    }

    return {
        radionuclideTotalDose:
            parseFloat(radioPharmItem.RadionuclideTotalDose) || undefined,
        radionuclideHalfLife:
            parseFloat(radioPharmItem.RadionuclideHalfLife) || undefined,
        radiopharmaceuticalStartTime:
            radioPharmItem.RadiopharmaceuticalStartTime ?? undefined,
        radiopharmaceuticalStartDateTime:
            radioPharmItem.RadiopharmaceuticalStartDateTime ?? undefined,
    };
}

export function dcmjsUpdateContourSequence(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    contours: DicomContour[]
) {
    const dataset = datasetWrapper.dataset as any;
    const roiSequenceItem = dataset.ROIContourSequence.find(
        (x: { ReferencedROINumber: number }) =>
            x.ReferencedROINumber === roiNumber
    );
    if (!roiSequenceItem) {
        throw new Error(
            `ROIContourSequence Item with ROINumber ${roiNumber} not found`
        );
    }

    // round the points to 3 decimals. DICOM does not like endless floating points
    const clampPoint = (point: ReadonlyVec3) => {
        return [
            roundToDecimals(point[0], 3),
            roundToDecimals(point[1], 3),
            roundToDecimals(point[2], 3),
        ];
    };
    roiSequenceItem.ContourSequence = contours.map((contour) => {
        return {
            ContourGeometricType: contour.type,
            NumberOfContourPoints: contour.length,
            ContourData: contour.data.map((point) => clampPoint(point)).flat(),
            ...(contour.referencedSOPInstanceUID !== undefined &&
            contour.referencedSOPClassUID !== undefined
                ? {
                      ContourImageSequence: {
                          ReferencedSOPClassUID: contour.referencedSOPClassUID,
                          ReferencedSOPInstanceUID:
                              contour.referencedSOPInstanceUID,
                      },
                  }
                : {}),
        };
    });
}

/**
 * Reassigns a DICOM sequence property as a fresh plain array copy of
 * its current contents. Use before mutating sequences parsed by dcmjs,
 * which wraps single-item SQ values in a Proxy that breaks Array.push.
 */
function unwrapDcmjsSequence(
    datasetWrapper: DcmJsDatasetWrapper,
    sequenceName: string
): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataset = datasetWrapper.dataset as any;
    const seq = dataset[sequenceName];
    if (seq == null) {
        dataset[sequenceName] = [];
        return;
    }
    if (Array.isArray(seq)) {
        // Index-based copy so reads go through the proxy's get trap
        // and return the real underlying items.
        const plain: unknown[] = [];
        for (let i = 0; i < seq.length; i++) {
            plain.push(seq[i]);
        }
        dataset[sequenceName] = plain;
    } else {
        dataset[sequenceName] = [seq];
    }
}

/**
 * Creates a new structure (ROI) within a DICOM structure set, including
 * inserting it into the DICOM dataset.
 *
 * @param dicomStructureSet - The DICOM structure set where the new ROI will be added into.
 */
export function dcmjsCreateNewStructure(
    dicomStructureSet: DicomStructureSet
): DicomRoi {
    const datasetWrapper = dicomStructureSet.datasetWrapper;
    const refSequence = (datasetWrapper.dataset as any)
        .ReferencedFrameOfReferenceSequence;
    if (
        !refSequence ||
        !Array.isArray(refSequence) ||
        refSequence.length === 0
    ) {
        throw new Error(
            `Missing or empty ReferencedFrameOfReferenceSequence in DICOM Structure Set (${dicomStructureSet.sopInstanceUid})`
        );
    }

    const frameOfReferenceUID = refSequence[0].FrameOfReferenceUID;

    if (!isUidLike(frameOfReferenceUID)) {
        throw new Error(
            `Invalid ReferencedFrameOfReference UID (${frameOfReferenceUID}) in DICOM Structure Set (${dicomStructureSet.sopInstanceUid})`
        );
    }

    // dcmjs wraps single-item SQ values in a Proxy whose `set` trap
    // routes numeric string writes onto the first element instead of
    // appending (dcmjs/build/dcmjs.js — addAccessors, tagProxyHandler).
    // An Array.push on such a proxy corrupts item 0 and leaves the
    // real array sparse, which then blows up datasetToDict on export.
    // Rebuilding the three RT sequences as plain arrays here breaks
    // out of the proxy so subsequent push operations behave normally.
    unwrapDcmjsSequence(datasetWrapper, 'StructureSetROISequence');
    unwrapDcmjsSequence(datasetWrapper, 'ROIContourSequence');
    unwrapDcmjsSequence(datasetWrapper, 'RTROIObservationsSequence');

    // create a new ROI with default settings
    const roi = new DicomRoi(
        dicomStructureSet.datasetWrapper,
        dicomStructureSet.getNextFreeRoiNumber(),
        generateUniqueStructureName(
            dicomStructureSet,
            localization.t('structure.defaultName13CharsMax')
        ),
        generateRandomDicomColor()
    );

    const { roiNumber } = roi;

    // StructureSetROISequence
    [
        ['ROINumber', roiNumber],
        ['ReferencedFrameOfReferenceUID', frameOfReferenceUID],
        ['ROIName', roi.name],
        ['ROIDescription', roi.roiDescription],
        ['ROIGenerationAlgorithm', roi.roiGenerationAlgorithm || ''],
    ].forEach((val) => {
        const [index, value] = val;
        dcmjsWriteRoiSequenceProperty(
            datasetWrapper,
            roiNumber,
            index as string,
            value,
            true
        );
    });

    // RTROIObservationsSequence
    [
        ['ObservationNumber', roi.roiNumber],
        ['ReferencedROINumber', roi.roiNumber],
        ['RTROIInterpretedType', roi.interpretedType || ''],
        ['ROIInterpreter', ''],
    ].forEach((val) => {
        const [index, value] = val;
        dcmjsWriteRoiObservationsProperty(
            datasetWrapper,
            roiNumber,
            index as string,
            value,
            true
        );
    });

    // ROIContourSequence
    [
        ['ROIDisplayColor', [roi.color.R, roi.color.G, roi.color.B]],
        ['ReferencedROINumber', roi.roiNumber],
        ['ContourSequence', []],
    ].forEach((val) => {
        const [index, value] = val;
        dcmjsWriteContourSequenceProperty(
            datasetWrapper,
            roiNumber,
            index as string,
            value,
            true
        );
    });

    // update this structure set to include the new ROI
    dicomStructureSet.addRoi(roi);

    // adjust ROI values
    roi.roiGenerationAlgorithm = RoiGenerationAlgorithmType.MANUAL;

    return roi;
}

/**
 * Removes a structure (ROI) from a DICOM structure set, including removing it from the DICOM dataset.
 * @param datasetWrapper - The DICOM dataset wrapper containing the structure set to remove the ROI from.
 * @param roiNumber - The ROINumber of the ROI to remove.
 */
export function dcmjsRemoveStructure(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number
) {
    const dataset = datasetWrapper.dataset as any;

    const withoutRoi = (sequence: any, key: string): any[] => {
        if (!sequence) {
            return [];
        }
        const arr = Array.isArray(sequence) ? [...sequence] : [sequence];
        return arr.filter(
            (item: any) => item != null && item[key] !== roiNumber
        );
    };

    dataset.StructureSetROISequence = withoutRoi(
        dataset.StructureSetROISequence,
        'ROINumber'
    );
    dataset.RTROIObservationsSequence = withoutRoi(
        dataset.RTROIObservationsSequence,
        'ReferencedROINumber'
    );
    dataset.ROIContourSequence = withoutRoi(
        dataset.ROIContourSequence,
        'ReferencedROINumber'
    );
}

// Helper functions to read/write properties of an ROI from different sequences in the DICOM dataset (StructureSetROISequence, RTROIObservationsSequence, ROIContourSequence).
const findRoiInRoiSequence = (
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number
) => {
    const seq = (datasetWrapper.dataset as any).StructureSetROISequence;
    if (!seq) {
        return undefined;
    }
    const arr = Array.isArray(seq) ? seq : [seq];
    return arr.find((x: { ROINumber: number }) => x?.ROINumber === roiNumber);
};

export function dcmjsReadRoiSequenceProperty<T>(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyName: string
): T | undefined {
    const roi = findRoiInRoiSequence(datasetWrapper, roiNumber);
    return roi ? (roi[propertyName] as T) : undefined;
}

export function dcmjsWriteRoiSequenceProperty<T>(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyName: string,
    value: T,
    /** Allows inserting a new ROI if it does not exist. Defaults to false. */
    allowNewRoi: boolean = false
) {
    let roi = findRoiInRoiSequence(datasetWrapper, roiNumber);

    if (!roi) {
        if (allowNewRoi) {
            (datasetWrapper.dataset as any).StructureSetROISequence.push({
                ROINumber: roiNumber,
            });
            roi = findRoiInRoiSequence(datasetWrapper, roiNumber);
        } else {
            throw new Error(
                `ROI with ROINumber ${roiNumber} not found from StructureSetROISequence`
            );
        }
    }

    roi[propertyName] = value;
}

const findRoiInContourSequence = (
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number
) => {
    const seq = (datasetWrapper.dataset as any).ROIContourSequence;
    if (!seq) {
        return undefined;
    }
    const arr = Array.isArray(seq) ? seq : [seq];
    return arr.find(
        (x: { ReferencedROINumber: number }) =>
            x?.ReferencedROINumber === roiNumber
    );
};

export function dcmjsWriteContourSequenceProperty<T>(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyName: string,
    value: T,
    /** Allows inserting a new ROI if it does not exist. Defaults to false. */
    allowNewRoi: boolean = false
) {
    let roi = findRoiInContourSequence(datasetWrapper, roiNumber);

    if (!roi) {
        if (allowNewRoi) {
            (datasetWrapper.dataset as any).ROIContourSequence.push({
                ReferencedROINumber: roiNumber,
            });
            roi = findRoiInContourSequence(datasetWrapper, roiNumber);
        } else {
            throw new Error(
                `ROI with ROINumber ${roiNumber} not found from ROIContourSequence`
            );
        }
    }

    roi[propertyName] = value;
}

const findRoiInObservationsSequence = (
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number
) => {
    const seq = (datasetWrapper.dataset as any).RTROIObservationsSequence;
    if (!seq) {
        return undefined;
    }
    const arr = Array.isArray(seq) ? seq : [seq];
    return arr.find(
        (x: { ReferencedROINumber: number }) =>
            x?.ReferencedROINumber === roiNumber
    );
};

export function dcmjsReadRoiObservationsProperty<T>(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyName: string
): T | undefined {
    const roi = findRoiInObservationsSequence(datasetWrapper, roiNumber);
    return roi ? (roi[propertyName] as T) : undefined;
}

export function dcmjsWriteRoiObservationsProperty<T>(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyName: string,
    value: T,
    /** Allows inserting a new ROI if it does not exist. Defaults to false. */
    allowNewRoi: boolean = false
) {
    let roi = findRoiInObservationsSequence(datasetWrapper, roiNumber);

    if (!roi) {
        if (allowNewRoi) {
            (datasetWrapper.dataset as any).RTROIObservationsSequence.push({
                ReferencedROINumber: roiNumber,
            });
            roi = findRoiInObservationsSequence(datasetWrapper, roiNumber);
        } else {
            throw new Error(
                `ROI with ROINumber ${roiNumber} not found from RTROIObservationsSequence`
            );
        }
    }

    roi[propertyName] = value;
}

/**
 * Supported `ROIPhysicalProperty` (3006,00B2) type codes inside
 * `ROIPhysicalPropertiesSequence` (3006,00B0). Per DICOM standard more types
 * exist (e.g. `EFFECTIVE_Z`), but the application currently only supports
 * relative electron density and relative mass density overrides.
 */
export enum RoiPhysicalPropertyType {
    REL_ELEC_DENSITY = 'REL_ELEC_DENSITY',
    REL_MASS_DENSITY = 'REL_MASS_DENSITY',
}

type RoiPhysicalPropertyEntry = {
    ROIPhysicalProperty: RoiPhysicalPropertyType;
    ROIPhysicalPropertyValue: number;
};

/**
 * Reads the numeric value of a single ROI physical property
 * (`ROIPhysicalPropertyValue`, tag 3006,00B4) from the
 * `ROIPhysicalPropertiesSequence` (3006,00B0) of the given ROI.
 *
 * Returns `undefined` if no entry of `propertyType` is present.
 * Per DICOM standard the sequence may contain multiple entries of different
 * types; only the entry matching `propertyType` is consulted — others are
 * ignored.
 */
export function dcmjsReadRoiPhysicalPropertyValue(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyType: RoiPhysicalPropertyType
): number | undefined {
    const sequence = dcmjsReadRoiObservationsProperty<
        RoiPhysicalPropertyEntry[]
    >(datasetWrapper, roiNumber, 'ROIPhysicalPropertiesSequence');
    return sequence?.find((e) => e.ROIPhysicalProperty === propertyType)
        ?.ROIPhysicalPropertyValue;
}

/**
 * Writes or removes the entry for a single ROI physical property type in
 * `ROIPhysicalPropertiesSequence` (3006,00B0). Entries of other types are
 * preserved.
 *
 * Pass `undefined` or `NaN` as `propertyValue` to remove the entry of the
 * given type. If the sequence becomes empty it is still written as an empty
 * array so that downstream persistence observes the change.
 */
export function dcmjsWriteRoiPhysicalPropertyValue(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyType: RoiPhysicalPropertyType,
    propertyValue: number | undefined
) {
    const existing =
        dcmjsReadRoiObservationsProperty<RoiPhysicalPropertyEntry[]>(
            datasetWrapper,
            roiNumber,
            'ROIPhysicalPropertiesSequence'
        ) ?? [];
    const withoutThisType = existing.filter(
        (e) => e.ROIPhysicalProperty !== propertyType
    );
    const next =
        propertyValue !== undefined && !isNaN(propertyValue)
            ? [
                  ...withoutThisType,
                  {
                      ROIPhysicalProperty: propertyType,
                      ROIPhysicalPropertyValue: propertyValue,
                  },
              ]
            : withoutThisType;
    dcmjsWriteRoiObservationsProperty(
        datasetWrapper,
        roiNumber,
        'ROIPhysicalPropertiesSequence',
        next
    );
}

export function dcmjsGetString(
    datasetWrapper: DcmJsDatasetWrapper,
    tag: string
): string {
    return (datasetWrapper.dataset as any)[tag]
        ? (datasetWrapper.dataset as any)[tag]
        : '';
}

export function dcmjsSetString(
    datasetWrapper: DcmJsDatasetWrapper,
    tag: string,
    value: string
) {
    (datasetWrapper.dataset as any)[tag] = value;
}

/**
 * Reads a numeric DICOM tag from the dataset.
 * Handles both single values and arrays (returns first element).
 * Also handles string values by parsing them (some DICOM tags are stored as strings).
 */
export function dcmjsGetNumber(dataset: any, tag: string): number | undefined {
    const rawValue = dataset[tag];
    if (rawValue === undefined || rawValue === null) {
        return undefined;
    }
    const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const parsed = Number(value.split('\\')[0]);
        return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
}

/**
 * Reads WindowWidth (0028,1051) and WindowCenter (0028,1050) from a DICOM dataset.
 * Returns a WindowLevel object if both values are valid, undefined otherwise.
 */
export function dcmjsGetWindowLevel(dataset: any): WindowLevel | undefined {
    const width = dcmjsGetNumber(dataset, 'WindowWidth');
    const center = dcmjsGetNumber(dataset, 'WindowCenter');
    if (width === undefined || center === undefined || width <= 0) {
        return undefined;
    }
    return new WindowLevel(width, center);
}

/**
 * Generates a unique label for a structure set by ensuring it does not conflict
 * with existing labels in the provided list of structure sets. The label is
 * truncated to a maximum of 16 characters, and if necessary, a numeric suffix
 * (e.g., "_1", "_12") is appended to make it unique.
 *
 * @param structureSets - An array of `StructureSet` objects, each containing a `structureSetLabel`.
 * @param label - The base label to use for generating the unique structure set label.
 * @returns A unique label string that does not conflict with existing labels.
 * @throws An error if a unique label cannot be generated after 99 attempts.
 */
/** Normalizes a DICOM short string: trims whitespace and replaces backslashes
 * with dashes. Backslashes are reserved as escape sequences in DICOM VR types
 * and cannot appear in short string values. */
export function normalizeDicomShortString(label: string): string {
    return label.replaceAll('\\', '-').trim();
}

export function generateUniqueStructureSetLabel(
    structureSets: StructureSet[],
    label: string
): string {
    const fixedLabel = normalizeDicomShortString(label);
    const existingLabels = structureSets.map((ss) => ss.structureSetLabel);
    const uniqueLabel = fixedLabel.substring(0, 16); // Ensure the label is max 16 characters

    if (!existingLabels.includes(uniqueLabel)) {
        return uniqueLabel;
    }

    let counter = 1;
    // arbitrary limit to avoid infinite loop
    while (counter < 100) {
        const substringEnd = counter < 10 ? 14 : 13; // Reserve space for "_X" or "_XX"
        const numberedLabel = `${fixedLabel.substring(0, substringEnd)}_${counter}`;
        if (!existingLabels.includes(numberedLabel)) {
            return numberedLabel;
        }
        counter++;
    }

    throw new Error(
        'Could not generate a unique structure set label. Too many structure sets with similar names exist.'
    );
}

export const STRUCTURE_NAME_MAX_LENGTH = 16;
const STRUCTURE_NAME_MAX_COUNTER = 100;

export function generateUniqueStructureNameFromList(
    existingNames: string[],
    name: string
): string {
    const fixedName = name.replaceAll('\\', '-').trim(); // Backslashes are not allowed in DICOM long strings
    const truncatedName = fixedName.substring(0, STRUCTURE_NAME_MAX_LENGTH);

    // Use domain-consistent name comparison
    const hasConflict = (candidate: string): boolean => {
        return existingNames
            .filter((n): n is string => n != null)
            .some((existing) =>
                DicomStructureSet.structureNamesEqual(existing, candidate)
            );
    };

    if (!hasConflict(truncatedName)) {
        return truncatedName;
    }

    for (let counter = 1; counter < STRUCTURE_NAME_MAX_COUNTER; counter++) {
        // Reserve space for "_" separator and counter digits
        const counterStr = counter.toString();
        const maxBaseLength = STRUCTURE_NAME_MAX_LENGTH - 1 - counterStr.length;
        const numberedName = `${fixedName.substring(0, maxBaseLength)}_${counter}`;
        if (!hasConflict(numberedName)) {
            return numberedName;
        }
    }

    throw new Error(
        'Could not generate a unique structure name. Too many structures with similar names exist.'
    );
}

export function generateUniqueStructureName(
    structureSet: DicomStructureSet,
    name: string
): string {
    const existingNames = Array.from(structureSet.rois.values()).map(
        (roi) => roi.name
    );
    return generateUniqueStructureNameFromList(existingNames, name);
}

function getSliceResolution(gridFrameOffsetVector: Array<number>): number {
    if (gridFrameOffsetVector.length < 2) {
        throw new Error(
            'GridFrameOffsetVector must have at least 2 entries' +
                ` to compute slice resolution, got` +
                ` ${gridFrameOffsetVector.length}.`
        );
    }
    if (Math.abs(gridFrameOffsetVector[0]) > 1e-4) {
        // we only support Grid Frame Offset Vector interpretation 'a.' from DICOM standard
        throw new Error(
            `Unexpected grid frame offset of ${gridFrameOffsetVector[0]} for the first slice. Expected 0.`
        );
    }
    const res = gridFrameOffsetVector[1] - gridFrameOffsetVector[0];
    const absRes = Math.abs(res);
    for (let i = 1; i < gridFrameOffsetVector.length; i++) {
        const actualStep = Math.abs(
            gridFrameOffsetVector[i] - gridFrameOffsetVector[i - 1]
        );
        if (Math.abs(actualStep - absRes) > 0.1) {
            throw new Error(
                `Unexpected grid frame offset of` +
                    ` ${actualStep} between slices` +
                    ` ${i - 1} and ${i}. Expected ${absRes}.`
            );
        }
    }
    return absRes;
}

function dcmjsToPlan(dataset: any): DicomPlan {
    CheckAndThrow(
        dataset.RTPlanGeometry === 'PATIENT',
        `Unsupported RTPlanGeometry '${dataset.RTPlanGeometry}'. SOP UID ${dataset.SOPInstanceUID}`,
        'RTPlanGeometry'
    );

    const rtStructReference = dataset.ReferencedStructureSetSequence
        ? dataset.ReferencedStructureSetSequence[0].ReferencedSOPInstanceUID
        : '';

    const plan = new DicomPlan(
        dataset.SOPInstanceUID,
        dataset.RTPlanLabel,
        rtStructReference,
        new DcmJsDatasetWrapper(dataset)
    );

    return plan;
}

function CheckMandatory(dataset: any, dicomTag: string) {
    if (dataset[dicomTag] === undefined) {
        throw new Error(
            `Mandatory DICOM tag ${dicomTag} is missing in the dataset ${dataset.Modality}: ${dataset.SOPInstanceUID}.`
        );
    }
}

function CheckAndThrow(
    condition: boolean,
    errorMessage: string,
    dicomTag: string
) {
    if (!condition) {
        throw new Error(`DICOM tag ${dicomTag}: ${errorMessage}`);
    }
}

function importDvhSequence(dvhSequence: any): DicomDvh[] {
    if (!dvhSequence) {
        return [];
    }
    const isSupportedDvh = (dvh: any) => {
        return (
            dvh.DVHReferencedROISequence &&
            dvh.DVHReferencedROISequence.length === 1 &&
            dvh.DVHReferencedROISequence[0].DVHROIContributionType ===
                'INCLUDED' &&
            dvh.DoseUnits === 'GY' &&
            dvh.DoseType !== 'ERROR' &&
            dvh.DVHType === 'CUMULATIVE'
        );
    };
    const supportedDvhs = dvhSequence?.filter(isSupportedDvh);
    const nonSupportedDvhs = dvhSequence?.filter(
        (dvh: any) => !isSupportedDvh(dvh)
    );
    if (nonSupportedDvhs?.length) {
        console.warn(
            `Found ${nonSupportedDvhs.length} DVHs that are not supported.`
        );
    }

    return supportedDvhs?.map((dvh: any) => {
        return new DicomDvh(
            strToDoseUnit(dvh.DoseUnits),
            strToDoseType(dvh.DoseType),
            strToDvhType(dvh.DVHType),
            strToVolumeUnits(dvh.DVHVolumeUnits),
            dvh.DVHNumberOfBins,
            dvh.DVHDoseScaling,
            dvh.DVHData,
            dvh.DVHReferencedROISequence[0].ReferencedROINumber,
            dvh.DVHMinimumDose,
            dvh.DVHMeanDose,
            dvh.DVHMaximumDose
        );
    });
}

export const exportedForTesting = {
    getSliceResolution,
};

function isDoseMatrixOrEmptyArray(arr?: unknown[]): arr is DoseMatrix[] {
    return (
        Array.isArray(arr) &&
        (arr.length === 0 || arr.every((item) => item instanceof DoseMatrix))
    );
}

function isStructureSetOrEmptyArray(arr?: unknown[]): arr is StructureSet[] {
    return (
        Array.isArray(arr) &&
        (arr.length === 0 || arr.every((item) => item instanceof StructureSet))
    );
}

function isRigidRegistrationOrEmptyArray(
    arr?: unknown[]
): arr is RigidRegistration[] {
    return (
        Array.isArray(arr) &&
        (arr.length === 0 ||
            arr.every((item) => item instanceof RigidRegistration))
    );
}
